import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import ExtensionsService from 'services/ServiceEXTENSIONS';
import * as yup from 'yup';

/**
 * @typedef {Object} PjsipConfiguration
 * @property {number} id
 * @property {string} name
 * @property {string} bind
 */

/**
 * @typedef {Object} Contexto
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {string} type
 */

/**
 * @typedef {Object} Extension
 * @property {number} id
 * @property {string} extension_number
 * @property {string} identifier
 * @property {string} secret
 * @property {number} call_limit
 * @property {string} callgroup
 * @property {string} pickupgroup
 * @property {string} transport
 * @property {boolean} allow_monitor_extension
 * @property {boolean} allow_internal_call_recording
 * @property {boolean} allow_external_call_recording
 * @property {string} type
 * @property {boolean} nat
 * @property {boolean} disable
 * @property {number} cost_center
 * @property {number} context
 * @property {number} pjsip_configuration_id
 * @property {boolean} blf
 * @property {PjsipConfiguration | null} pjsip_configuration
 * @property {Contexto | null} contexto
 */

/**
 * Hook para buscar ramais com opções de paginação e filtragem.
 * @param {Object} [props={}]
 * @param {boolean} [props.showDisabled=false] - Indica se deve incluir ramais desabilitados.
 * @param {number} [props.itemsPerPage=10] - Número de itens por página na paginação.
 * @param {boolean} [props.showAll=false] - Se true, ignora a paginação e busca todos os ramais.
 * @returns
 */
const useExtensions = (props = {}) => {
  const {
    showDisabled: initialShowDisabled = false,
    itemsPerPage = 10,
    showAll: initialShowAll = false
  } = props;

  const [state, setState] = useState({
    /** @type {Extension[]} */
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0,
    activePage: 1,
    searchText: '',
    showDisabled: initialShowDisabled,
    showAll: initialShowAll
  });

  const fetchData = useCallback(
    async (
      selectedPage = state.activePage,
      search = state.searchText,
      showDisabledParam = state.showDisabled,
      showAllParam = state.showAll
    ) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        let extensionList = [];
        let totalRegisters = 0;

        if (showAllParam) {
          const response = await ExtensionsService.getAllShowDisable(
            showDisabledParam
          );
          // A API retorna um objeto de resposta. A lista de extensões está na propriedade `data.data`.
          // Validamos a propriedade `data.data` com o schema de array.
          const validatedList = await extensionListSchema.validate(
            response?.data?.data,
            { abortEarly: false }
          );
          extensionList = validatedList;
          totalRegisters = extensionList.length;
        } else {
          const response = await ExtensionsService.getAllSeach(
            selectedPage,
            itemsPerPage,
            search,
            showDisabledParam
          );
          // Validação dos dados. A lista de extensões está na propriedade `data.data`.
          const validatedList = await extensionListSchema.validate(
            response?.data?.data,
            { abortEarly: false }
          );
          extensionList = validatedList;
          totalRegisters = response?.data?.totalRegisters ?? 0;
        }

        setState((prevState) => ({
          ...prevState,
          response: extensionList,
          totalRegisters: totalRegisters,
          isLoading: false,
          isError: false,
          error: null,
          activePage: selectedPage,
          searchText: search,
          showDisabled: showDisabledParam,
          showAll: showAllParam
        }));
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? 'Não foi possível carregar os ramais'
            : err.response?.data?.message || 'Erro ao carregar ramais';

        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });

        setState((prevState) => ({
          ...prevState,
          response: [],
          totalRegisters: 0,
          isLoading: false,
          isError: true,
          error: err
        }));
      }
    },
    [
      state.activePage,
      state.searchText,
      state.showDisabled,
      state.showAll,
      itemsPerPage
    ]
  );

  const fetch = useCallback(
    (page, search, showDisabled) => {
      // O método fetch agora é apenas um wrapper para a função fetchData.
      // Ele garante que o showAll seja respeitado.
      fetchData(page, search, showDisabled, state.showAll);
    },
    [fetchData, state.showAll]
  );

  const handlePageChange = useCallback(
    (page) => {
      if (!state.showAll) {
        fetch(page, state.searchText, state.showDisabled);
      }
    },
    [fetch, state.searchText, state.showDisabled, state.showAll]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setState((prevState) => ({
        ...prevState,
        searchText: value
      }));
      fetch(1, value, state.showDisabled);
    },
    [fetch, state.showDisabled]
  );

  const handleShowDisabledChange = useCallback(
    (value) => {
      setState((prevState) => ({
        ...prevState,
        showDisabled: value
      }));
      // Se showAll for true, a página não é redefinida para 1.
      fetch(state.showAll ? state.activePage : 1, state.searchText, value);
    },
    [fetch, state.activePage, state.searchText, state.showAll]
  );

  /**
   * Busca um ramal pelo ID e valida a resposta com o esquema Yup.
   * @param {string} id - O ID do ramal a ser buscado.
   * @returns {Promise<Extension|null>} O ramal encontrado ou null em caso de erro.
   */
  const fetchExtensionById = useCallback(async (id) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));
    try {
      const response = await ExtensionsService.findById(id);
      const validatedResponse = await extensionResponseSchema.validate(
        response.data,
        { abortEarly: false }
      );

      if (validatedResponse.status === 'OK' && validatedResponse.data) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: false,
          error: null
        }));
        return validatedResponse.data;
      } else {
        throw new Error(
          validatedResponse.message || 'Dados do ramal não encontrados.'
        );
      }
    } catch (err) {
      console.error({ err });
      const errorMessage =
        err.name === 'ValidationError'
          ? 'Não foi possível carregar os ramais'
          : err.response?.data?.message || 'Erro ao carregar ramais';

      toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true
      });
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        error: err
      }));
      return null;
    }
  }, []);

  const options = useMemo(
    () =>
      state.response.map(({ extension_number }) => ({
        label: extension_number,
        value: extension_number
      })),
    [state.response]
  );

  useEffect(() => {
    // Chamada inicial para carregar os dados
    fetchData(1, '', initialShowDisabled, initialShowAll);
  }, [fetchData, initialShowDisabled, initialShowAll]);

  return {
    ...state,
    fetch,
    handlePageChange,
    handleSearchChange,
    handleShowDisabledChange,
    fetchExtensionById,
    options
  };
};

export default useExtensions;

const pjsipConfigurationSchema = yup.object().shape({
  id: yup.number().integer().required(),
  name: yup.string().required(),
  bind: yup.string().required()
});

const contextoSchema = yup.object().shape({
  id: yup.number().integer().required(),
  name: yup.string().required(),
  description: yup.string().required(),
  type: yup.string().required()
});

const extensionSchema = yup.object().shape({
  extension_number: yup.string().required('Número de extensão é obrigatório'),
  identifier: yup.string().nullable(),
  secret: yup.string().required('Secret é obrigatório'),
  call_limit: yup
    .number()
    .integer()
    .min(1, 'Limite de chamadas deve ser no mínimo 1')
    .required('Limite de chamadas é obrigatório'),
  callgroup: yup.string().nullable(),
  pickupgroup: yup.string().nullable(),
  transport: yup
    .string()
    .oneOf(['UDP', 'TCP', 'TLS'], 'Transporte inválido')
    .required('Transporte é obrigatório'),
  allow_monitor_extension: yup
    .boolean()
    .required('Permissão para monitorar extensão é obrigatória'),
  allow_internal_call_recording: yup
    .boolean()
    .required('Permissão para gravação interna é obrigatória'),
  allow_external_call_recording: yup
    .boolean()
    .required('Permissão para gravação externa é obrigatória'),
  type: yup
    .string()
    // .oneOf(['PJSIP', 'SIP'], 'Tipo inválido')
    .required('Tipo é obrigatório'),
  nat: yup.boolean().required('NAT é obrigatório'),
  disable: yup.boolean().required('Status de desabilitado é obrigatório'),
  cost_center: yup.number().integer().nullable(),
  context: yup.number().integer().required('Contexto é obrigatório'),
  pjsip_configuration_id: yup.number().integer().nullable(),
  blf: yup.boolean().required('BLF é obrigatório'),
  pjsip_configuration: pjsipConfigurationSchema.nullable(),
  contexto: contextoSchema.required()
});

const extensionListSchema = yup.array().of(extensionSchema);

const extensionResponseSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['OK', 'ERROR'], 'Status inválido')
    .required('Status é obrigatório'),
  message: yup.string().nullable(),
  data: extensionSchema.nullable(),
  errors: yup.array().of(yup.string()).defined('Erros são obrigatórios')
});
